import { useEffect, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import Layout from "@/components/Layout";
import TraditionalCostSlider from "@/components/TraditionalCostSlider";
import WebsiteCheatsheet from "@/components/WebsiteCheatsheet";
import ProductSample from "@/components/ProductSample";
import DIYFirstFooter from "@/components/DIYFirstFooter";
import MusicStudioSignBlock from "@/components/MusicStudioSignBlock";
import { DIY_FOOTER_SUPPRESSED } from "@/lib/sageLinks";
import { api, formatApiError } from "@/lib/api";
import { useNavigate } from "react-router-dom";

function money(cents) {
  return `$${(cents / 100).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
}

export default function ProductDetail() {
  const { slug } = useParams();
  const [searchParams] = useSearchParams();
  const [groups, setGroups] = useState([]);
  const [buying, setBuying] = useState("");
  const [paymentsOpen, setPaymentsOpen] = useState(true);
  const [paypalReady, setPaypalReady] = useState(true);
  const navigate = useNavigate();
  const ref = searchParams.get("ref"); // cross-promotion attribution

  useEffect(() => {
    api.get("/products").then((r) => setGroups(r.data.products || [])).catch(() => {});
    api.get("/payments/status")
      .then((r) => setPaymentsOpen(r.data.enabled !== false))
      .catch(() => setPaymentsOpen(true));
    // PayPal keys may not be seated (dev pod / pre-flight). When ready=false,
    // every Buy button routes to /contact as an inquiry so nothing dead-ends.
    api.get("/paypal/config")
      .then((r) => setPaypalReady(!!r.data.ready))
      .catch(() => setPaypalReady(false));
  }, []);

  const group = groups.find((g) => g.slug === slug);

  const buy = async (lookup_key, item) => {
    // Founder plain-ink 2026-02-15: when PayPal keys aren't seated yet, the
    // Buy button falls through to /contact with a pre-filled inquiry so
    // buyers never hit a dead 502/503. Same for the inquiry-mode products.
    if (!paypalReady) {
      const subject = `Order inquiry · ${item?.name || lookup_key}`;
      const message =
        `Product: ${item?.name || lookup_key}\n` +
        `Slug: ${slug}\n` +
        `Lookup key: ${lookup_key}\n\n` +
        `Payment rail is being seated. Please queue my order and reach back with a rail link.`;
      navigate(
        `/contact?subject=${encodeURIComponent(subject)}&product_slug=${encodeURIComponent(slug)}&message=${encodeURIComponent(message)}`
      );
      return;
    }
    setBuying(lookup_key);
    try {
      const { data } = await api.post("/payments/paypal/create", {
        lookup_key,
        quantity: 1,
        origin_url: window.location.origin,
        ref: ref || undefined,
      });
      if (data.approve_url) {
        window.location.href = data.approve_url;
      } else {
        toast.error("PayPal did not return an approve link.");
      }
    } catch (err) {
      const msg = formatApiError(err.response?.data?.detail) || "Could not start checkout.";
      toast.error(msg);
    } finally {
      setBuying("");
    }
  };

  if (!group) {
    return (
      <Layout>
        <div className="max-w-[900px] mx-auto px-6 py-32 text-center">
          <p className="font-mono text-xs uppercase tracking-[0.32em] text-[#52525b]">
            Loading — or that page hasn&apos;t been forged yet.
          </p>
          <Link
            to="/products"
            className="inline-block mt-8 font-mono text-[11px] uppercase tracking-[0.32em] text-[#ff5a00] hover:text-[#ff8f00]"
          >
            ← Back to Products
          </Link>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      {!paymentsOpen && (
        <div
          data-testid="payments-closed-banner"
          className="w-full bg-[#0b0b0d] border-b border-[#ff5a00]/40 py-3 px-6 text-center"
        >
          <p className="font-mono text-[10px] uppercase tracking-[0.32em] text-[#ff5a00]">
            Orders queued —{" "}
            <Link to="/contact" className="underline hover:text-[#ffbb00]">
              reach out via /contact
            </Link>
            {" "}and we&apos;ll open your commission when the rail is live.
          </p>
        </div>
      )}
      {/* Hero */}
      <section
        data-testid={`product-detail-${group.slug}`}
        className="relative pt-24 pb-16 sm:pt-32 sm:pb-20 border-b border-white/[0.05] overflow-hidden"
      >
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[1200px] h-[600px] opacity-[0.08] blur-3xl"
               style={{ background: "radial-gradient(circle at 50% 0%,#ff5a00,transparent 60%)" }} />
        </div>
        <div className="relative z-10 max-w-[1100px] mx-auto px-6 sm:px-10">
          <Link
            to="/products"
            className="inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.32em] text-[#52525b] hover:text-[#ff5a00]"
          >
            <span>←</span> All Products
          </Link>
          <p className="font-mono text-[11px] uppercase tracking-[0.45em] text-[#ff5a00] mt-8">
            Product {String(group.order).padStart(2, "0")}
          </p>
          <h1
            data-testid="product-headline"
            className="font-display uppercase leading-[0.92] mt-4 text-[#ededed]"
            style={{ fontSize: "clamp(2.5rem, 7vw, 5.5rem)", letterSpacing: "-0.005em" }}
          >
            {group.name}
          </h1>
          <p className="font-display italic text-xl sm:text-3xl text-[#ededed] mt-6 max-w-3xl leading-tight">
            {group.hero_line}
          </p>
          <p className="font-body text-base sm:text-lg text-[#a1a1aa] mt-6 max-w-3xl leading-relaxed">
            {group.long}
          </p>
        </div>
      </section>

      {/* Traditional cost comparison */}
      <section className="max-w-[1100px] mx-auto px-6 sm:px-10 pt-16 pb-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.7 }}
          data-testid="traditional-cost"
          className="relative border border-[#ff5a00]/25 bg-gradient-to-br from-[#0b0b0d] to-[#141010] px-6 sm:px-10 py-8"
        >
          <p className="font-mono text-[10px] uppercase tracking-[0.42em] text-[#ff5a00]">
            The Old Way vs. The Forge
          </p>
          <p className="font-display italic text-lg sm:text-2xl text-[#ededed] mt-4 max-w-4xl leading-snug">
            {group.traditional_cost}
          </p>
        </motion.div>
      </section>

      {/* Interactive slider */}
      <TraditionalCostSlider slug={group.slug} />

      {/* Per-product sample — the real proof-of-quality asset */}
      <ProductSample slug={group.slug} />

      {/* Website-specific cheatsheet */}
      {group.slug === "website" && <WebsiteCheatsheet />}

      {/* Items */}
      <section className="max-w-[1100px] mx-auto px-6 sm:px-10 pb-24">
        <p className="font-mono text-[11px] uppercase tracking-[0.42em] text-[#52525b] mb-8">
          {group.items.length > 1 ? "Choose your rung" : "The offering"}
        </p>
        <div className="grid gap-5">
          {group.items.map((it, i) => (
            <motion.div
              key={it.lookup_key}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.6, delay: i * 0.04 }}
              data-testid={`product-item-${it.lookup_key}`}
              className={`relative border ${
                it.highlight
                  ? "border-[#ff5a00]/60 bg-gradient-to-br from-[#12100c] to-[#0b0b0d]"
                  : "border-[#27272a] bg-[#0b0b0d]/60"
              } px-6 sm:px-8 py-6 sm:py-8`}
            >
              {it.highlight && (
                <span className="absolute -top-3 left-6 bg-[#ff5a00] text-[#050505] font-mono text-[9px] uppercase tracking-[0.32em] px-2 py-0.5">
                  Best value
                </span>
              )}
              <div className="grid grid-cols-1 md:grid-cols-[1fr_auto] gap-6 items-start">
                <div>
                  <h3 className="font-display text-2xl sm:text-3xl text-[#ededed] leading-tight">
                    {it.name}
                  </h3>
                  <p className="font-body text-[15px] text-[#a1a1aa] mt-2 leading-relaxed">
                    {it.blurb}
                  </p>
                  <ul className="grid sm:grid-cols-2 gap-y-1.5 gap-x-6 mt-4">
                    {it.deliverables.map((d) => (
                      <li key={d} className="font-mono text-[11px] uppercase tracking-[0.18em] text-[#a1a1aa] flex gap-2 items-start">
                        <span className="text-[#ff5a00] mt-0.5">·</span>
                        <span>{d}</span>
                      </li>
                    ))}
                  </ul>
                  <p className="font-mono text-[10px] uppercase tracking-[0.32em] text-[#52525b] mt-4">
                    Turnaround: {it.timeline}
                  </p>
                </div>
                <div className="flex flex-col items-start md:items-end gap-3 md:min-w-[220px]">
                  <span
                    data-testid={`price-${it.lookup_key}`}
                    className="font-display text-[#ededed] leading-none"
                    style={{ fontSize: "clamp(2rem, 4vw, 3rem)" }}
                  >
                    {money(it.amount_cents)}
                  </span>
                  {/* Products with checkout_mode="inquiry" route to /contact
                      with a pre-filled subject/message from the catalog. Buyers
                      never hit PayPal for these; the Founder screens them
                      personally. Any future custom offering can opt in via
                      the same flag on paypal_bridge.CATALOG. */}
                  {group.checkout_mode === "inquiry" ? (
                    <Link
                      data-testid={`inquiry-${group.slug}`}
                      to={`/contact?subject=${encodeURIComponent(group.inquiry_subject || `${group.name} inquiry`)}&product_slug=${encodeURIComponent(group.slug)}&message=${encodeURIComponent(group.inquiry_message || "")}`}
                      className="font-mono text-[10px] uppercase tracking-[0.32em] bg-[#ff5a00] text-[#050505] px-6 py-3 hover:bg-[#ff8f00] transition-colors text-center"
                    >
                      {group.inquiry_cta || `Book ${group.name}`}
                    </Link>
                  ) : (
                    <>
                      <span
                        data-testid={`crypto-discount-${it.lookup_key}`}
                        className="font-mono text-[9px] uppercase tracking-[0.28em] text-[#3DE0C8] border border-[#3DE0C8]/50 px-2 py-1"
                        title="Pay in USDC via Purser (Polygon / Base / Ethereum / Solana) — 3% off the card price."
                      >
                        or {money(Math.round(it.amount_cents * 0.97))} · USDC (−3%)
                      </span>
                      <button
                        data-testid={`buy-${it.lookup_key}`}
                        onClick={() => paymentsOpen && buy(it.lookup_key, it)}
                        disabled={buying === it.lookup_key || !paymentsOpen}
                        title={paymentsOpen ? "" : "Not accepting new orders — reach out via /contact and we'll queue your commission."}
                        className="font-mono text-[10px] uppercase tracking-[0.32em] bg-[#ff5a00] text-[#050505] px-6 py-3 hover:bg-[#ff8f00] transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                      >
                        {!paymentsOpen
                          ? "Orders queued — see /contact"
                          : buying === it.lookup_key
                          ? "Opening PayPal…"
                          : !paypalReady
                          ? "Order (inquiry)"
                          : "Order via PayPal"}
                      </button>
                    </>
                  )}
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        <p className="font-mono text-[10px] uppercase tracking-[0.32em] text-[#52525b] mt-10">
          10% of every paid order flows to the Glass Ledger causes ·
          <Link to="/#ledger" className="ml-2 text-[#ff5a00] hover:text-[#ff8f00]">see the ledger →</Link>
        </p>
      </section>

      <DIYFirstFooter slug={group.slug} suppressed={DIY_FOOTER_SUPPRESSED.has(group.slug)} />
      {group.slug === "score-voice" && <MusicStudioSignBlock />}
    </Layout>
  );
}
